/**
 * Mobile Layout Component
 * 
 * Provides the basic layout structure for mobile pages.
 * Includes mobile-optimized header, navigation, and content areas.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import apiHelper from '../../utils/apiHelper';
import './MobileLayout.css';
import { AuthContext } from '../../components/Auth/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';

// Simple in-memory cache for profile images
const profileImageCache = new Map();

/**
 * Mobile Layout Component
 * 
 * Provides consistent mobile layout with header, navigation, and content
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title
 * @param {boolean} props.showBackButton - Whether to show back button
 * @param {function} props.onBackClick - Custom back button handler (optional)
 * @param {string} props.backButtonIcon - Custom back button icon (default: home)
 * @returns {JSX.Element} Mobile layout with children
 */
const MobileLayout = ({ children, title = 'Irish Funds', showBackButton = false, onBackClick, backButtonIcon = 'home', infoTooltip }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  
  // Use centralized profile status from context
  const { profile: profileData, loading: profileLoading } = useProfile();
  
  // Clean up expired cache entries on mount
  useEffect(() => {
    const now = Date.now();
    const cacheExpiry = 10 * 60 * 1000; // 10 minutes
    
    for (const [key, value] of profileImageCache.entries()) {
      const cacheAge = now - value.timestamp;
      if (cacheAge > cacheExpiry) {
        URL.revokeObjectURL(value.imageUrl);
        profileImageCache.delete(key);
      }
    }
  }, []);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { user: [user] } = useContext(AuthContext);

  // Profile data now comes from useProfileStatus hook - no separate API call needed

  useEffect(() => {
    const handleProfileImageChange = () => {
      // Profile data will be automatically updated by useProfileStatus hook
      // Just trigger a re-render by clearing the cached image
      setProfileImage(null);
    };

    window.addEventListener('profileImageChanged', handleProfileImageChange);

    return () => {
      window.removeEventListener('profileImageChanged', handleProfileImageChange);
    };
  }, []); // No dependencies needed - just sets up event listener

  // Profile image loading effect (matches HeaderSettings logic)
  useEffect(() => {
    const loadProfileImage = async () => {
      if (!profileData?.photo) return;

      try {
        // Check in-memory cache first
        const cacheKey = `${user?.profile?.email}_${profileData.photo}`;
        const cachedData = profileImageCache.get(cacheKey);
        
        if (cachedData) {
          const { imageUrl, timestamp } = cachedData;
          const cacheAge = Date.now() - timestamp;
          const cacheExpiry = 10 * 60 * 1000; // 10 minutes
          
          if (cacheAge < cacheExpiry) {
            // Use cached image
            setProfileImage(imageUrl);
            return;
          } else {
            // Clean up old cache entry
            URL.revokeObjectURL(imageUrl);
            profileImageCache.delete(cacheKey);
          }
        }

        // Check if API is ready before making calls
        const authStatus = apiHelper.checkAuthStatus();
        if (!authStatus.tokenGetterInitialized) {
          return;
        }

        const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profileData.photo}`;
        const authToken = await apiHelper.getAuthToken();
        
        const response = await fetch(imageUrl, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          if (response.status !== 404) {
            console.error('Error loading profile image:', response.statusText);
          }
          setProfileImage(null);
          return;
        }

        const blob = await response.blob();
        const imageObjectUrl = URL.createObjectURL(blob);
        setProfileImage(imageObjectUrl);

        // Cache the image URL in memory
        profileImageCache.set(cacheKey, {
          imageUrl: imageObjectUrl,
          timestamp: Date.now()
        });

      } catch (error) {
        if (error.message && !error.message.includes('404') && !error.message.includes('Auth0 token getter not initialized')) {
          console.error('Error loading profile image:', error);
        }
        setProfileImage(null);
      }
    };

    loadProfileImage();

    return () => {
      // Clean up current profile image URL
      if (profileImage && profileImage.startsWith('blob:')) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileData?.photo]);

  const getInitials = (name) => {
    if (!name) return '';
    const nameParts = name.split(' ');
    const initials = nameParts.map(part => part.charAt(0).toUpperCase()).join('');
    return initials;
  };

  const userName = user?.profile?.name || '';
  const initials = getInitials(userName);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleProfileClick = () => {
    if (profileData?.id) {
      navigate('/mobile/profile', { 
        state: { 
          profileData: profileData
        }
      });
    }
  };

  const handleHomeClick = () => {
    navigate('/mobile');
  };

  // Mobile menu items
  const menuItems = [
    { path: '/mobile', label: 'Home', icon: 'fas fa-home' },
    { path: '/mobile/news', label: 'News', icon: 'fas fa-newspaper' },
    { path: '/mobile/events', label: 'Events', icon: 'fas fa-calendar' },
    { path: '/mobile/policy', label: 'Policy & Regulatory', icon: 'fas fa-gavel' },
    { path: '/mobile/institutional-intelligence', label: 'Institutional Intelligence', icon: 'intelligence-icon' },
    { path: '/mobile/groups', label: 'Working Groups', icon: 'fas fa-users' },
    { path: '/mobile/innovation', label: 'Innovation', icon: 'fas fa-lightbulb' },
    { path: '/mobile/council', label: 'Council Corner', icon: 'fas fa-building' },
    { path: '/mobile/media', label: 'Media Centre', icon: 'fas fa-play-circle' },
    { path: '/mobile/etf', label: 'ETF Screener', icon: 'fas fa-book' },
            { path: '/mobile/daire', label: 'Dáire', icon: 'daire-icon' },
    { path: '/mobile/search', label: 'Search', icon: 'fas fa-search' }
  ];

  return (
    <div className="mobile-layout">
      {/* Mobile Header */}
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="mobile-header-left">
            {showBackButton ? (
              <button 
                className="mobile-back-btn"
                onClick={onBackClick || (() => navigate('/mobile'))}
              >
                <i className={`fas fa-${backButtonIcon}`}></i>
              </button>
            ) : (
              <>
                {location.pathname === '/mobile' && (
                  <div className="mobile-logo-container">
                    <img src="/assets/img/IFlogo-white-small.png" alt="Irish Funds" className="mobile-header-logo" />
                  </div>
                )}
                {location.pathname !== '/mobile' && (
                  <button 
                    className="mobile-home-btn"
                    onClick={handleHomeClick}
                  >
                    <i className="fas fa-home"></i>
                  </button>
                )}
              </>
            )}
          </div>
          
          <div className="mobile-header-center">
            {location.pathname !== '/mobile' && (
              <h1 className="mobile-title">
                {title}
                {infoTooltip && (
                  <Tippy content={infoTooltip}>
                    <i
                      className="fas fa-circle-info"
                      style={{ fontSize: '0.6em', marginLeft: '6px', verticalAlign: 'top', cursor: 'pointer', opacity: 0.85 }}
                      aria-label="More information"
                    />
                  </Tippy>
                )}
              </h1>
            )}
          </div>
          
          <div className="mobile-header-right">
            <button 
              className="mobile-profile-btn"
              // onClick={handleProfileClick}
              title={userName}
            >
              {profileImage ? (
                <img 
                  src={profileImage}
                  alt={userName}
                  className="mobile-profile-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="mobile-profile-initials">${initials}</span>`;
                  }}
                />
              ) : (
                <span className="mobile-profile-initials">{initials}</span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay - Removed since no hamburger menu */}
      {/* {isMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMenu}>
          <nav className="mobile-menu">
            <div className="mobile-menu-content">
              <div className="mobile-menu-header">
                <img src="/images/logo.png" alt="Irish Funds" className="mobile-logo" />
                <button className="mobile-menu-close" onClick={closeMenu}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <ul className="mobile-menu-items">
                {menuItems.map((item) => (
                  <li key={item.path}>
                    <Link 
                      to={item.path} 
                      className={`mobile-menu-item ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={closeMenu}
                    >
                      {item.icon === 'daire-icon' ? (
                        <img src="/assets/img/daire/daire-logo-icon.png" alt="Daire AI" className="daire-icon" />
                      ) : item.icon === 'intelligence-icon' ? (
                        <img
                          src="/images/intelligence-centre-icon-256.png"
                          alt="Institutional Intelligence"
                          style={{ width: '36px', height: '36px', objectFit: 'contain' }}
                        />
                      ) : (
                        <i className={item.icon}></i>
                      )}
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </div>
      )} */}

      {/* Main Content */}
      <main className="mobile-content">
        {children}
      </main>
      
      {/* Mobile Footer */}
      <footer className="mobile-footer">
        <div className="mobile-footer-content">
          <p className="mobile-footer-desktop"> 🖥️ Best viewed on desktop</p>
          <p className="mobile-footer-credits">Crafted by Infinia.ie</p>
        </div>
      </footer>
    </div>
  );
};

export default MobileLayout; 